import React from "react";
import { BlockMath } from "react-katex";
import CodeBlock from "../components/CodeBlock";
import MermaidDiagram from "../components/MermaidDiagram";
import ImageWithCaption from "../components/ImageWithCaption";
import RedAlert from "../components/RedAlert";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "../components/ui/table";
import { cn } from "../lib/utils";
import { findContentBlocks } from "./contentBlocks";
import { convertMdxTableToHtml } from "../lib/problemAnswerUtils";
import { processTextContent } from "./textContentUtils";

function renderTextBlockWithMarkdown(
  content: string,
  blockKey: number,
  options?: { enableDebug?: boolean; isInsideWrappingBlock?: boolean; footnotes?: Map<string, string> }
): React.ReactNode {
  const lines = content.split("\n");
  const elements: React.ReactNode[] = [];
  let currentTextLines: string[] = [];
  let lineKey = 0;

  const flushText = () => {
    if (currentTextLines.length > 0) {
      const text = currentTextLines.join("\n");
      if (text.trim()) {
        elements.push(
          <div
            key={`${blockKey}-text-${lineKey++}`}
            className="whitespace-pre-wrap"
            style={
              options?.isInsideWrappingBlock
                ? { width: "fit-content" }
                : undefined
            }
          >
            {processTextContent(text, {
              enableDebug: options?.enableDebug,
              footnotes: options?.footnotes,
            })}
          </div>
        );
      }
      currentTextLines = [];
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();

    // 수평선 (--- 또는 ___ 또는 ***)
    if (/^(-{3,}|_{3,}|\*{3,})$/.test(trimmed)) {
      flushText();
      elements.push(
        <hr
          key={`${blockKey}-hr-${lineKey++}`}
          className="my-4 border-gray-300 dark:border-gray-600"
        />
      );
      continue;
    }

    // 헤딩 (## ~ ######)
    const headingMatch = trimmed.match(/^(#{2,6})\s+(.+)$/);
    if (headingMatch) {
      flushText();
      const level = headingMatch[1].length;
      const headingText = headingMatch[2];
      const className =
        level === 2
          ? "text-lg font-bold mt-6 mb-2 dark:text-white"
          : level === 3
            ? "text-base font-bold mt-4 mb-1 dark:text-white"
            : "text-sm font-bold mt-3 mb-1 dark:text-white";

      const HeadingTag =
        `h${level}` as keyof React.JSX.IntrinsicElements;
      elements.push(
        <HeadingTag
          key={`${blockKey}-h${level}-${lineKey++}`}
          className={className}
        >
          {processTextContent(headingText, {
            enableDebug: options?.enableDebug,
          })}
        </HeadingTag>
      );
      continue;
    }

    currentTextLines.push(line);
  }

  flushText();

  return elements.length === 1 ? elements[0] : <>{elements}</>;
}

/**
 * 텍스트에서 각주 정의([^n]: 설명)를 추출하고, 본문에서 제거한 텍스트를 반환
 */
function extractFootnotes(text: string): { cleanedText: string; footnotes: Map<string, string> } {
  const footnotes = new Map<string, string>();
  const footnoteDefRegex = /^\[\^(\d+)\]:\s*(.+)$/gm;
  let match;

  while ((match = footnoteDefRegex.exec(text)) !== null) {
    footnotes.set(match[1], match[2]);
  }

  if (footnotes.size === 0) {
    return { cleanedText: text, footnotes };
  }

  // 각주 정의 라인 제거 + 연속 빈 줄 정리
  const cleanedText = text
    .replace(/^\[\^\d+\]:\s*.+$/gm, "")
    .replace(/\n{3,}/g, "\n\n");

  return { cleanedText, footnotes };
}

interface RenderMixedContentOptions {
  componentId?: string; // mermaid ID 생성용
  enableDebug?: boolean; // processTextContent 디버깅 옵션
  isInsideWrappingBlock?: boolean; // wrapping-block 내부인지 여부
  footnotes?: Map<string, string>; // 각주 정의 맵
  inProblem?: boolean; // 문제 내부 코드블록 여부 (흑백 토글 활성화)
}

const normalizeTableCellContent = (content: string): string =>
  content.replace(/<br\s*\/?>/gi, "\n");

/**
 * 혼합 콘텐츠(표 + LaTeX + 텍스트)를 렌더링하는 함수
 * @param text 렌더링할 텍스트
 * @param options 옵션 (componentId: mermaid ID 생성용, enableDebug: 디버깅 옵션)
 * @returns React 노드
 */
export function renderMixedContent(
  text: string,
  options?: RenderMixedContentOptions
): React.ReactNode {
  // 최상위 호출에서만 각주 추출 (재귀 호출 시에는 이미 추출된 footnotes를 사용)
  let processedText = text;
  let footnotes = options?.footnotes;
  if (!footnotes) {
    const extracted = extractFootnotes(text);
    processedText = extracted.cleanedText;
    footnotes = extracted.footnotes.size > 0 ? extracted.footnotes : undefined;
  }

  const blocks = findContentBlocks(processedText);

  return (
    <>
      {blocks.map((block, index) => {
        if (block.type === "wrapping-block") {
          return (
            <div key={index} className={cn("my-4 flex flex-col items-center", block.className)}>
              {renderMixedContent(block.content, { ...options, isInsideWrappingBlock: true, footnotes })}
            </div>
          );
        } else if (block.type === "table") {
          const tableData = convertMdxTableToHtml(block.content);
          if (!tableData) return null;

          return (
            <div key={index} className={cn("my-4 overflow-x-auto", block.className)}>
              <Table>
                <TableHeader>
                  <TableRow>
                    {tableData.headers.map((header, headerIndex) => (
                      <TableHead
                        key={headerIndex}
                        className="whitespace-pre-line break-words align-middle"
                        style={{
                          textAlign: tableData.alignments[
                            headerIndex
                          ] as React.CSSProperties["textAlign"],
                        }}
                      >
                        {processTextContent(
                          normalizeTableCellContent(header),
                          {
                            enableDebug: options?.enableDebug,
                            footnotes,
                          }
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableData.dataRows.map((row, rowIndex) => (
                    <TableRow key={rowIndex}>
                      {row!.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          className="whitespace-pre-line break-words align-top"
                          style={{
                            textAlign: tableData.alignments[
                              cellIndex
                            ] as React.CSSProperties["textAlign"],
                          }}
                        >
                          {processTextContent(
                            normalizeTableCellContent(cell),
                            {
                              enableDebug: options?.enableDebug,
                              footnotes,
                            }
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          );
        } else if (block.type === "block-math") {
          return (
            <div key={index} className="my-4 text-center">
              <BlockMath math={block.content} />
            </div>
          );
        } else if (block.type === "code") {
          return (
            <CodeBlock
              key={index}
              data-language={block.language}
              data-code-text={block.content}
              inProblem={options?.inProblem}
            >
              <code className={`language-${block.language}`}>
                {block.content}
              </code>
            </CodeBlock>
          );
        } else if (block.type === "mermaid") {
          // componentId가 있으면 problem용 ID, 없으면 mdx용 ID
          const mermaidId = options?.componentId
            ? `mermaid-problem-${options.componentId.replace(/:/g, "-")}-${index}`
            : `mermaid-mdx-${index}`;

          return (
            <MermaidDiagram
              key={index}
              chart={block.content}
              id={mermaidId}
            />
          );
        } else if (
          block.type === "image" &&
          block.props &&
          block.props.src &&
          typeof block.props.alt === "string"
        ) {
          return (
            <ImageWithCaption
              key={index}
              src={block.props.src as string}
              alt={block.props.alt as string}
              caption={block.props.caption as string | undefined}
              width={block.props.width as number | undefined}
              height={block.props.height as number | undefined}
              maxWidth={block.props.maxWidth as number | undefined}
              showCaption={block.props.showCaption as boolean | undefined}
            />
          );
        } else if (block.type === "red-alert" && block.props) {
          return (
            <RedAlert
              key={index}
              title={block.props.title as string | undefined}
              description={block.props.description as string | undefined}
              color={block.props.color as string | undefined}
              className={block.props.className as string | undefined}
            />
          );
        } else {
          const hasMarkdownBlock = /^(#{2,6}\s|---$|___$|\*\*\*$)/m.test(
            block.content
          );

          if (hasMarkdownBlock) {
            return (
              <div
                key={index}
                style={{
                  ...(options?.isInsideWrappingBlock
                    ? { width: "fit-content" }
                    : {}),
                  marginBottom:
                    block.content.trim() && index < blocks.length - 1
                      ? "1rem"
                      : "0",
                }}
              >
                {renderTextBlockWithMarkdown(block.content, index, {
                  enableDebug: options?.enableDebug,
                  isInsideWrappingBlock: options?.isInsideWrappingBlock,
                  footnotes,
                })}
              </div>
            );
          }

          // wrapping-block 내부의 텍스트 블록은 div로 렌더링하되 width를 fit-content로 설정
          if (options?.isInsideWrappingBlock) {
            return (
              <div
                key={index}
                className="whitespace-pre-wrap"
                style={{
                  width: "fit-content",
                  marginBottom:
                    block.content.trim() && index < blocks.length - 1
                      ? "1rem"
                      : "0",
                }}
              >
                {processTextContent(block.content, {
                  enableDebug: options?.enableDebug,
                  footnotes,
                })}
              </div>
            );
          }

          return (
            <div
              key={index}
              className="whitespace-pre-wrap"
              style={{
                marginBottom:
                  block.content.trim() && index < blocks.length - 1
                    ? "1rem"
                    : "0",
              }}
            >
              {processTextContent(block.content, {
                enableDebug: options?.enableDebug,
                footnotes,
              })}
            </div>
          );
        }
      })}
    </>
  );
}
